export interface JwtPart {
  header: Record<string, unknown>;
  payload: Record<string, unknown>;
  signature: string;
}

export interface JwtDecodeResult {
  success: boolean;
  data?: JwtPart;
  error?: string;
}

/**
 * Decode JWT token (does NOT verify signature)
 */
export function decodeJwt(token: string): JwtDecodeResult {
  try {
    const segments = token.trim().split('.');
    if (segments.length !== 3) {
      return { success: false, error: 'Invalid JWT format. Expected 3 segments separated by dots.' };
    }

    const [headerB64, payloadB64, signature] = segments;

    // Decode header
    const headerJson = base64UrlDecode(headerB64);
    const header = JSON.parse(headerJson);

    // Decode payload
    const payloadJson = base64UrlDecode(payloadB64);
    const payload = JSON.parse(payloadJson);

    return {
      success: true,
      data: { header, payload, signature },
    };
  } catch (e) {
    return { success: false, error: (e as Error).message };
  }
}

/**
 * Base64 URL decode (JWT uses URL-safe Base64)
 */
function base64UrlDecode(str: string): string {
  // Convert URL-safe Base64 to standard Base64
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/');

  // Add padding if needed
  const padding = base64.length % 4;
  if (padding) {
    base64 += '='.repeat(4 - padding);
  }

  // Decode
  const decoded = atob(base64);

  // Handle UTF-8
  return decodeURIComponent(
    decoded.split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
  );
}

/**
 * Check if JWT is expired
 */
export function isJwtExpired(payload: Record<string, unknown>): boolean {
  if (!payload.exp) return false;
  return (payload.exp as number) * 1000 < Date.now();
}

/**
 * Get JWT expiration date
 */
export function getJwtExpiration(payload: Record<string, unknown>): Date | null {
  if (!payload.exp) return null;
  return new Date((payload.exp as number) * 1000);
}

/**
 * Generate UUID v4
 */
export function generateUuidV4(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Generate UUID v7 (time-ordered)
 */
export function generateUuidV7(): string {
  const timestamp = Date.now();
  const timeHex = timestamp.toString(16).padStart(12, '0');
  const random = generateUuidV4().replace(/-/g, '').substring(8);
  return `${timeHex.substring(0, 8)}-${timeHex.substring(8, 12)}-7${random.substring(1, 4)}-${random.substring(4, 8)}-${random.substring(8, 20)}`;
}

/* -------------------------------------------------------------------------- */
/* Hash Generator                                                              */
/* -------------------------------------------------------------------------- */

export type HashAlgorithm = 'SHA-1' | 'SHA-256' | 'SHA-384' | 'SHA-512';

export interface HashResult {
  success: boolean;
  data?: string;
  error?: string;
}

export const SUPPORTED_HASH_ALGORITHMS: HashAlgorithm[] = ['SHA-1', 'SHA-256', 'SHA-384', 'SHA-512'];

/**
 * Generate cryptographic hash using Web Crypto API.
 * Note: MD5 is intentionally not supported — it is cryptographically broken.
 */
export async function generateHash(input: string, algorithm: HashAlgorithm): Promise<HashResult> {
  if (!SUPPORTED_HASH_ALGORITHMS.includes(algorithm)) {
    return { success: false, error: `Unsupported algorithm: ${algorithm}` };
  }

  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hashBuffer = await crypto.subtle.digest(algorithm, data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    return { success: true, data: hex };
  } catch (e) {
    return { success: false, error: (e as Error).message };
  }
}

/**
 * Verify a string against an expected hash. Returns true if they match.
 */
export async function verifyHash(
  input: string,
  expectedHash: string,
  algorithm: HashAlgorithm
): Promise<{ success: boolean; match: boolean; error?: string }> {
  const normalizedExpected = expectedHash.trim().toLowerCase().replace(/\s+/g, '');
  if (!normalizedExpected) {
    return { success: false, match: false, error: 'Expected hash is empty.' };
  }

  const result = await generateHash(input, algorithm);
  if (!result.success || !result.data) {
    return { success: false, match: false, error: result.error };
  }

  return {
    success: true,
    match: result.data.toLowerCase() === normalizedExpected,
  };
}

/* -------------------------------------------------------------------------- */
/* Password Generator                                                          */
/* -------------------------------------------------------------------------- */

export interface PasswordOptions {
  length: number;
  uppercase: boolean;
  lowercase: boolean;
  numbers: boolean;
  symbols: boolean;
  excludeAmbiguous: boolean;
}

export interface PasswordResult {
  success: boolean;
  data?: string;
  error?: string;
  /** Computed character pool size — useful for entropy estimation. */
  poolSize?: number;
}

const POOL = {
  uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  uppercaseSafe: 'ABCDEFGHJKMNPQRSTUVWXYZ', // exclude I, L, O
  lowercase: 'abcdefghijklmnopqrstuvwxyz',
  lowercaseSafe: 'abcdefghijkmnpqrstuvwxyz', // exclude l, o
  numbers: '0123456789',
  numbersSafe: '23456789', // exclude 0, 1
  symbols: '!@#$%^&*()_+-=[]{}|;:,.<>?',
};

function getRandomChar(chars: string): string {
  // crypto.getRandomValues for unbiased uniform randomness.
  const arr = new Uint32Array(1);
  crypto.getRandomValues(arr);
  return chars[arr[0] % chars.length];
}

function shuffle(input: string): string {
  const arr = input.split('');
  for (let i = arr.length - 1; i > 0; i--) {
    const r = new Uint32Array(1);
    crypto.getRandomValues(r);
    const j = r[0] % (i + 1);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr.join('');
}

export function generatePassword(options: PasswordOptions): PasswordResult {
  const {
    length = 16,
    uppercase = true,
    lowercase = true,
    numbers = true,
    symbols = true,
    excludeAmbiguous = false,
  } = options;

  if (length < 4 || length > 128) {
    return { success: false, error: 'Length must be between 4 and 128 characters.' };
  }

  const upperChars = excludeAmbiguous ? POOL.uppercaseSafe : POOL.uppercase;
  const lowerChars = excludeAmbiguous ? POOL.lowercaseSafe : POOL.lowercase;
  const numberChars = excludeAmbiguous ? POOL.numbersSafe : POOL.numbers;
  const symbolChars = POOL.symbols;

  const required: string[] = [];
  if (uppercase) required.push(upperChars);
  if (lowercase) required.push(lowerChars);
  if (numbers) required.push(numberChars);
  if (symbols) required.push(symbolChars);

  if (required.length === 0) {
    return { success: false, error: 'Select at least one character type.' };
  }

  const poolSize = required.reduce((sum, set) => sum + set.length, 0);
  const pool = required.join('');

  // Guarantee at least one character from each enabled set.
  let password = required.map((set) => getRandomChar(set)).join('');

  while (password.length < length) {
    password += getRandomChar(pool);
  }

  // Shuffle so guaranteed chars are not always at the start.
  const final = shuffle(password.slice(0, length));

  return { success: true, data: final, poolSize };
}

/**
 * Estimate password entropy in bits: log2(poolSize^length).
 */
export function estimateEntropy(length: number, poolSize: number): number {
  if (poolSize <= 1 || length <= 0) return 0;
  return length * Math.log2(poolSize);
}

/* -------------------------------------------------------------------------- */
/* Hash Verifier                                                               */
/* -------------------------------------------------------------------------- */

export interface HashVerifyResult {
  success: boolean;
  match?: boolean;
  computed?: string;
  error?: string;
}

/**
 * Convenience wrapper around verifyHash that also returns the computed hash
 * so the UI can show what was generated for comparison.
 */
export async function verifyHashDetailed(
  input: string,
  expectedHash: string,
  algorithm: HashAlgorithm
): Promise<HashVerifyResult> {
  const result = await generateHash(input, algorithm);
  if (!result.success || !result.data) {
    return { success: false, error: result.error };
  }
  const normalized = expectedHash.trim().toLowerCase().replace(/\s+/g, '');
  return {
    success: true,
    computed: result.data,
    match: result.data.toLowerCase() === normalized,
  };
}
